import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../api.js';
import { useI18n } from '../../i18n/index.jsx';
import { useLive } from '../../live.jsx';
import { Alert } from '../../components/ui.jsx';
import { WaIcon } from '../../components/WhatsApp.jsx';
import { MessageBubble } from './CustomerWhatsApp.jsx';
import { fmtDate } from './ws.jsx';

// Mon espace › WhatsApp › "Nouveaux contacts / New contacts" (migration 043):
// numbers that wrote to PME's WhatsApp and match no customer. Reply (within
// 24 h), then link the number to an existing customer or create one — its
// messages move to that customer's timeline.
export default function WhatsAppInbox() {
  const { t, lang, errorText } = useI18n();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState(null);
  const [sel, setSel] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);
  const [create, setCreate] = useState({ companyName: '', contactName: '' });
  const [error, setError] = useState('');

  useEffect(() => { api('/workspace/whatsapp/status').then(setStatus).catch(() => setStatus({ usable: false })); }, []);
  const load = useCallback(() => api('/workspace/whatsapp/inbox').then((d) => setItems(d.items)).catch((e) => setError(errorText(e))), [errorText]);
  const open = useCallback((n) => api(`/workspace/whatsapp/inbox/${n}`).then(setThread).catch((e) => setError(errorText(e))), [errorText]);
  useEffect(() => { if (status?.inbox) load(); }, [status, load]);
  useEffect(() => { if (sel) open(sel.number); }, [sel, open]);
  useLive(['whatsapp'], () => { if (status?.inbox) { load(); if (sel) open(sel.number); } });
  useEffect(() => {
    if (q.trim().length < 2) { setFound([]); return; }
    const tm = setTimeout(() => api(`/workspace/customers?q=${encodeURIComponent(q.trim())}`).then((d) => setFound((d.items || d.customers || d).slice?.(0, 8) || [])).catch(() => setFound([])), 250);
    return () => clearTimeout(tm);
  }, [q]);

  async function reply() {
    setError('');
    try { await api(`/workspace/whatsapp/inbox/${sel.number}/reply`, { method: 'POST', body: { text } }); setText(''); open(sel.number); } catch (e) { setError(errorText(e)); }
  }
  async function link(body) {
    setError('');
    try { const r = await api(`/workspace/whatsapp/inbox/${sel.number}/link`, { method: 'POST', body }); navigate(`/account/workspace/customers/${r.crmId}?tab=whatsapp`); } catch (e) { setError(errorText(e)); }
  }

  const head = <div className="acc-head"><div><h1><WaIcon size={24} /> {t('wa.inboxTitle')}</h1><p className="muted">{t('wa.inboxLead')}</p></div></div>;
  if (!status) return <div className="ws-page">{head}<p className="muted">{t('common.loading')}</p></div>;
  if (!status.inbox) return <div className="ws-page">{head}<p className="callout callout-info">{status.apiReady ? t('wa.inboxNoRight') : t('wa.notConfigured')}</p></div>;

  return (
    <div className="ws-page">
      {head}
      <Alert>{error}</Alert>
      {items && !items.length && <p className="muted">{t('wa.inboxEmpty')}</p>}
      {items && items.length > 0 && (
        <div className="wa-inbox">
          <ul>
            {items.map((x) => (
              <li key={x.number}><button type="button" className={sel?.number === x.number ? 'on' : ''} onClick={() => { setSel(x); setThread(null); }}>
                <strong>{x.name || `+${x.number}`}</strong>
                <span className="small muted">+{x.number} · {fmtDate(x.at, lang, true)} · {x.count}</span>
                <span className="small" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.last}</span>
              </button></li>
            ))}
          </ul>
          <section className="ws-card">
            {!sel && <p className="muted">{t('wa.inboxPick')}</p>}
            {sel && thread && (
              <>
                <h2 style={{ marginTop: 0 }}>{sel.name || `+${sel.number}`}</h2>
                <div className="wa-thread">{thread.messages.map((m) => <MessageBubble key={m.id} m={m} lang={lang} t={t} />)}</div>
                {thread.window.open ? (
                  <div className="wa-compose">
                    <textarea aria-label={t('wa.message')} value={text} onChange={(e) => setText(e.target.value)} placeholder={t('wa.messagePh')} />
                    <div><button type="button" className="btn btn-wa btn-sm" disabled={!text.trim()} onClick={reply}>{t('wa.send')}</button></div>
                  </div>
                ) : <p className="wa-window closed">{t('wa.windowClosed')}</p>}
                <h3>{t('wa.linkTitle')}</h3>
                <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('wa.linkSearch')} aria-label={t('wa.linkSearch')} />
                {found.length > 0 && (
                  <ul className="ws-mini-list" style={{ listStyle: 'none', padding: 0 }}>
                    {found.map((c) => <li key={c.id}><button type="button" className="btn btn-link btn-sm" onClick={() => link({ crmId: c.id })}>🔗 {c.company || c.contact}</button></li>)}
                  </ul>
                )}
                <p className="small muted">{t('wa.orCreate')}</p>
                <div className="ws-row">
                  <input value={create.companyName} onChange={(e) => setCreate((x) => ({ ...x, companyName: e.target.value }))} placeholder={t('ws.priv.companyName')} aria-label={t('ws.priv.companyName')} />
                  <input value={create.contactName} onChange={(e) => setCreate((x) => ({ ...x, contactName: e.target.value || sel.name || '' }))} placeholder={sel.name || t('ws.priv.contactName')} aria-label={t('ws.priv.contactName')} />
                  <button type="button" className="btn btn-primary btn-sm" disabled={!create.companyName.trim() && !create.contactName.trim() && !sel.name}
                    onClick={() => link({ create: { companyName: create.companyName, contactName: create.contactName || sel.name || '' } })}>{t('wa.createContact')}</button>
                </div>
                <p className="small"><Link to="/account/workspace/customers">{t('ws.cp.back')}</Link></p>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
